/**
 * CollectedPhotosTab
 *
 * Tab "Ảnh thu thập" - Hiển thị danh sách ảnh đính kèm trong Collect Items.
 * Dùng flow cũ: fetch forms → fetch file UUIDs → fetch file details.
 * Location được parse từ metadata HOẶC từ CollectItem.geom (WKT POINT).
 */
import { memo, useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import {
  Table,
  Button,
  Tooltip,
  Spin,
  Empty,
  Input,
  DatePicker,
  Space,
  Switch,
  message,
  Image,
  Dropdown,
  Tag,
} from 'antd';
import type { MenuProps } from 'antd';
import {
  ReloadOutlined,
  ExpandOutlined,
  CompressOutlined,
  CloseOutlined,
  EnvironmentOutlined,
  UserOutlined,
  DownloadOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { PiImages } from 'react-icons/pi';
import { useAtom, useAtomValue } from 'jotai';
import { currentProjectAtom } from '~/stores/ProjectStore';
import { showCollectPhotoVectorTilesAtom } from '~/components/Map/stores/mapStore';
import { CollectItemFileAPIs, CollectItemFileDownloadVariant } from '~/common/typescript/apis/CollectItemFileAPIs';
import { CollectItemAPIs } from '~/common/typescript/apis/CollectItemAPIs';
import { remote_CollectForm_getPage } from '~/common/typescript/apis/CollectFormAPIs';
import { getAxiosInstance } from '~/common/react/utils/networkUtil';
import { API_URL_ADMIN } from '~/config/config';
import { useScreenSize } from '~/hooks/useScreenSize';
import DateTimeViewer from '~/components/common/DateTimeViewer';

const { RangePicker } = DatePicker;
const { Search } = Input;

/* ── Types ─────────────────────────────────────────────────────────────────── */

interface FileMetadata {
  location?: {
    coords?: {
      latitude: number;
      longitude: number;
      accuracy?: number;
      altitude?: number;
    };
    timestamp?: number;
  };
  compassHeading?: number;
  address?: string;
}

interface CollectedPhotoRow {
  uuid: string;
  collect_item_uuid: string;
  collect_form_uuid: string;
  metadata?: string;
  created_at?: string;
  user_id?: number;
  file_name?: string;
  displayName: string;
  parsedMeta: FileMetadata | null;
  location: { latitude: number; longitude: number } | null;
  /** Parsed properties object từ CollectItem */
  collectItemProps?: Record<string, any>;
  collectItemType?: string;
}

/* ── Helpers ────────────────────────────────────────────────────────────────── */

const parseMetadata = (metaStr?: string): FileMetadata | null => {
  if (!metaStr) return null;
  try { return JSON.parse(metaStr); } catch { return null; }
};

const getDisplayName = (uuid: string, fileName?: string): string => {
  if (fileName) return fileName;
  const parts = uuid.split('__');
  return parts.length > 1 ? parts.slice(1).join('__') : uuid;
};

/**
 * geom từ DB là GeoJSON string: {"type":"Point","coordinates":[lng,lat]}
 * hoặc WKT fallback: "POINT(lng lat)"
 */
const parseGeomToLocation = (geom?: string): { latitude: number; longitude: number } | null => {
  if (!geom) return null;
  // Try GeoJSON first
  try {
    const parsed = typeof geom === 'string' ? JSON.parse(geom) : geom;
    if (parsed?.type === 'Point' && Array.isArray(parsed.coordinates) && parsed.coordinates.length >= 2) {
      const lng = parsed.coordinates[0];
      const lat = parsed.coordinates[1];
      if (isFinite(lat) && isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        return { latitude: lat, longitude: lng };
      }
    }
  } catch { /* not GeoJSON, try WKT */ }
  // Fallback: WKT POINT(lng lat)
  const match = String(geom).match(/POINT\s*\(\s*([-\d.]+)\s+([-\d.]+)\s*\)/i);
  if (match) {
    const lng = parseFloat(match[1]);
    const lat = parseFloat(match[2]);
    if (isFinite(lat) && isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      return { latitude: lat, longitude: lng };
    }
  }
  return null;
};

const getLocation = (meta: FileMetadata | null, geom?: string): { latitude: number; longitude: number } | null => {
  // 1. Metadata GPS (từ camera của thiết bị mobile)
  if (meta) {
    const lat = meta.location?.coords?.latitude ?? (meta.location as any)?.latitude;
    const lng = meta.location?.coords?.longitude ?? (meta.location as any)?.longitude;
    if (lat != null && lng != null && isFinite(Number(lat)) && isFinite(Number(lng))) {
      return { latitude: Number(lat), longitude: Number(lng) };
    }
  }
  // 2. CollectItem geometry (điểm thu thập)
  return parseGeomToLocation(geom);
};

const getLocationText = (meta: FileMetadata | null): string => {
  if (!meta) return '—';
  if (meta.address) return meta.address;
  const lat = meta.location?.coords?.latitude ?? (meta.location as any)?.latitude;
  const lng = meta.location?.coords?.longitude ?? (meta.location as any)?.longitude;
  if (lat != null && lng != null) return `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`;
  return '—';
};

/* ── Hook: auth image fetch for CollectItemFile ─────────────────────────────── */

const useCollectFileBlob = (uuid?: string, variant: CollectItemFileDownloadVariant = 'thumbnail') => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!uuid) { setBlobUrl(null); return; }
    let cancelled = false;
    let objectUrl: string | null = null;

    setLoading(true);
    const params: Record<string, string> = {};
    if (variant === 'thumbnail') params['x-oss-process'] = 'image/resize,w_300';
    else if (variant === 'hd')   params['x-oss-process'] = 'image/resize,w_hd';
    else if (variant === 'stamp') params['stamp'] = 'true';

    const qs = Object.keys(params).length ? '?' + new URLSearchParams(params).toString() : '';

    getAxiosInstance()
      .get(`/api/geocollect/restore/download-file/${uuid}${qs}`, { responseType: 'blob', baseURL: API_URL_ADMIN })
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setBlobUrl(objectUrl);
      })
      .catch(() => { if (!cancelled) setBlobUrl(null); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, variant]);

  return { blobUrl, loading };
};

/* ── Thumbnail Cell ─────────────────────────────────────────────────────────── */

const ThumbnailCell = memo(({
  uuid,
  name,
  onPreview,
}: {
  uuid: string;
  name: string;
  onPreview: () => void;
}) => {
  const [inView, setInView] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setInView(true); obs.disconnect(); }
    }, { rootMargin: '100px' });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const { blobUrl, loading } = useCollectFileBlob(inView ? uuid : undefined, 'thumbnail');

  return (
    <UI_ThumbWrapper ref={ref} onClick={onPreview} title="Xem ảnh lớn">
      {loading && !blobUrl && (
        <div style={{ width: 60, height: 60, background: '#f1f5f9', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin size="small" />
        </div>
      )}
      {blobUrl && (
        <img src={blobUrl} alt={name} style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4, display: 'block', cursor: 'zoom-in' }} />
      )}
      {!loading && !blobUrl && (
        <div style={{ width: 60, height: 60, background: '#f1f5f9', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <PiImages style={{ color: '#94a3b8', fontSize: 20 }} />
        </div>
      )}
    </UI_ThumbWrapper>
  );
});

/* ── Main Component ─────────────────────────────────────────────────────────── */

interface CollectedPhotosTabProps {
  tableHeight?: string;
  onMaximize?: () => void;
  onClose?: () => void;
  isMaximized?: boolean;
}

const CollectedPhotosTab = ({ tableHeight, onMaximize, onClose, isMaximized }: CollectedPhotosTabProps) => {
  const currentProject = useAtomValue(currentProjectAtom);
  const { isMobile } = useScreenSize();
  // Vector tile toggle — controls CollectPhotoVectorTileController visibility
  const [showOnMap, setShowOnMap] = useAtom(showCollectPhotoVectorTilesAtom);

  const [allRows, setAllRows]       = useState<CollectedPhotoRow[]>([]);
  const [loading, setLoading]       = useState(false);
  const [pagination, setPagination]  = useState({ page: 1, pageSize: 20, total: 0 });

  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange]   = useState<[string, string] | null>(null);

  const [previewUuid, setPreviewUuid]       = useState<string | null>(null);
  const [previewVariant, setPreviewVariant] = useState<CollectItemFileDownloadVariant>('stamp');
  const [previewVisible, setPreviewVisible] = useState(false);
  const { blobUrl: previewBlobUrl } = useCollectFileBlob(
    previewVisible && previewUuid ? previewUuid : undefined,
    previewVariant,
  );

  const fetchPhotos = useCallback(async (page = 1, pageSize = 20) => {
    if (!currentProject?.id) return;
    setLoading(true);
    try {
      const formsResp = await remote_CollectForm_getPage({ projectId: currentProject.id, page: 1, pageSize: 200 });
      const forms = formsResp.results ?? [];
      if (forms.length === 0) {
        setAllRows([]);
        setPagination(p => ({ ...p, page, pageSize, total: 0 }));
        return;
      }

      const allPhotos: CollectedPhotoRow[] = [];
      const collectItemCache = new Map<string, { props?: Record<string, any>; type?: string; geom?: string }>();

      await Promise.all(
        forms.map(async (form: any) => {
          try {
            const syncResp = await CollectItemFileAPIs.remote_getPageSyncable({
              collect_form_uuid: form.uuid,
              page: 1,
              pageSize: 500,
            });

            const uuids = (syncResp.results ?? []).map((s: any) => s.uuid);
            if (uuids.length === 0) return;

            const detailResp = await CollectItemFileAPIs.remote_getListWithUUIDs({
              collect_form_uuid: form.uuid,
              uuids,
            });

            const itemUuids = [...new Set(detailResp.map((f: any) => f.collect_item_uuid).filter(Boolean))];
            const itemsToFetch = itemUuids.filter((uuid: string) => !collectItemCache.has(uuid));
            
            if (itemsToFetch.length > 0) {
              try {
                const itemsResp = await CollectItemAPIs.remote_getCollectItemsWithUUIDs({
                  collect_form_uuid: form.uuid,
                  uuids: itemsToFetch,
                });
                itemsResp.forEach((item: any) => {
                  // properties là string JSON từ DB
                  let parsedProps: Record<string, any> = {};
                  try {
                    parsedProps = typeof item.properties === 'string'
                      ? JSON.parse(item.properties)
                      : (item.properties || {});
                  } catch { /* ignore */ }
                  collectItemCache.set(item.uuid, { 
                    props: parsedProps,
                    type: item.type,
                    geom: item.geom,  // GeoJSON string: {"type":"Point","coordinates":[lng,lat]}
                  });
                });
              } catch (e) {
                console.warn('Failed to fetch collect items:', e);
              }
            }

            detailResp.forEach((file: any) => {
              const parsedMeta = parseMetadata(file.metadata);
              const itemInfo = collectItemCache.get(file.collect_item_uuid) || {};
              const location = getLocation(parsedMeta, itemInfo.geom);
              const itemName = itemInfo.props?.name != null ? String(itemInfo.props.name) : undefined;
              allPhotos.push({
                uuid: file.uuid,
                collect_item_uuid: file.collect_item_uuid,
                collect_form_uuid: file.collect_form_uuid,
                metadata: file.metadata,
                created_at: file.created_at,
                user_id: file.user_id,
                file_name: file.name,
                displayName: getDisplayName(file.uuid, file.name),
                parsedMeta,
                location,
                collectItemProps: itemInfo.props,
                collectItemType: itemInfo.type,
              });
            });
          } catch { /* ignore per-form errors */ }
        }),
      );

      allPhotos.sort((a, b) => {
        const da = a.created_at ? new Date(a.created_at).getTime() : 0;
        const db = b.created_at ? new Date(b.created_at).getTime() : 0;
        return db - da;
      });

      let filtered = allPhotos;
      if (searchText.trim()) {
        const q = searchText.trim().toLowerCase();
        filtered = filtered.filter(r =>
          r.displayName.toLowerCase().includes(q) ||
          r.collect_item_uuid.toLowerCase().includes(q) ||
          (r.collectItemProps?.name != null ? String(r.collectItemProps.name) : '').toLowerCase().includes(q) ||
          getLocationText(r.parsedMeta).toLowerCase().includes(q),
        );
      }
      if (dateRange) {
        const [start, end] = dateRange;
        filtered = filtered.filter(r => {
          if (!r.created_at) return false;
          const d = new Date(r.created_at);
          return d >= new Date(start) && d <= new Date(end + 'T23:59:59');
        });
      }

      const total = filtered.length;
      const startIdx = (page - 1) * pageSize;
      const pageRows = filtered.slice(startIdx, startIdx + pageSize);

      setAllRows(pageRows);
      setPagination({ page, pageSize, total });
    } catch (err) {
      console.error('Failed to fetch collected photos:', err);
      message.error('Không thể tải danh sách ảnh thu thập');
    } finally {
      setLoading(false);
    }
  }, [currentProject?.id, searchText, dateRange]);

  useEffect(() => {
    fetchPhotos(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject?.id, searchText, dateRange]);

  const handleRowClick = (row: CollectedPhotoRow) => {
    if (row.location) {
      const map = window.map;
      if (map) map.flyTo({ center: [row.location.longitude, row.location.latitude], zoom: 16, essential: true, duration: 1000 });
    }
  };

  const handleDownload = (row: CollectedPhotoRow, variant: CollectItemFileDownloadVariant, e: React.MouseEvent) => {
    e.stopPropagation();
    const url = CollectItemFileAPIs.getCollectItemFileDownloadUrl(row.uuid, variant);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${row.displayName}${variant !== 'original' ? `_${variant}` : ''}`;
    link.target = '_blank';
    link.click();
  };

  const downloadMenuItems = (row: CollectedPhotoRow): MenuProps['items'] => [
    { key: 'original', label: 'Tải gốc', icon: <DownloadOutlined />, onClick: ({ domEvent }) => handleDownload(row, 'original', domEvent as any) },
    { key: 'thumbnail', label: 'Thumbnail (300px)', icon: <DownloadOutlined />, onClick: ({ domEvent }) => handleDownload(row, 'thumbnail', domEvent as any) },
    { key: 'hd', label: 'HD (1920px)', icon: <DownloadOutlined />, onClick: ({ domEvent }) => handleDownload(row, 'hd', domEvent as any) },
    { key: 'stamp', label: 'Ảnh stamp (GPS + thông tin)', icon: <DownloadOutlined />, onClick: ({ domEvent }) => handleDownload(row, 'stamp', domEvent as any) },
  ];

  const columns = [
    {
      title: 'Ảnh',
      key: 'thumbnail',
      width: 72,
      render: (_: any, record: CollectedPhotoRow) => (
        <ThumbnailCell
          uuid={record.uuid}
          name={record.displayName}
          onPreview={() => { setPreviewUuid(record.uuid); setPreviewVariant('stamp'); setPreviewVisible(true); }}
        />
      ),
    },
    {
      title: 'Đối tượng thu thập',
      key: 'collectItem',
      width: 140,
      render: (_: any, record: CollectedPhotoRow) => {
        const name = record.collectItemProps?.name;
        return (
          <UI_ItemName>
            {name != null ? (
              <Tag color="blue" style={{ margin: 0, fontSize: 10 }}>{String(name)}</Tag>
            ) : (
              <span style={{ color: '#94a3b8', fontSize: 11 }}>—</span>
            )}
          </UI_ItemName>
        );
      },
    },
    {
      title: 'Vị trí',
      key: 'location',
      render: (_: any, record: CollectedPhotoRow) => {
        // Ưu tiên address từ metadata, sau đó tọa độ từ location object (có thể từ geom)
        const address = record.parsedMeta?.address;
        const hasLoc = record.location != null;
        const locText = address
          ? address
          : hasLoc
            ? `${record.location!.latitude.toFixed(6)}, ${record.location!.longitude.toFixed(6)}`
            : '—';
        return (
          <UI_Location title={locText}>
            <EnvironmentOutlined style={{ flexShrink: 0, marginRight: 4, color: hasLoc ? '#3b82f6' : '#94a3b8' }} />
            <span style={{ color: hasLoc ? '#374151' : '#94a3b8' }}>{locText}</span>
          </UI_Location>
        );
      },
    },
    {
      title: 'Người chụp',
      key: 'user',
      width: 100,
      render: (_: any, record: CollectedPhotoRow) => (
        <UI_UserCell>
          <UserOutlined style={{ color: '#64748b', fontSize: 11 }} />
          <span>{record.user_id ? `#${record.user_id}` : '—'}</span>
        </UI_UserCell>
      ),
    },
    {
      title: 'Ngày',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 110,
      render: (date: string) => (
        <DateTimeViewer date={date} initialMode="relative" severity="secondary" showTooltip />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 64,
      render: (_: any, record: CollectedPhotoRow) => (
        <Space size={2} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Xem ảnh stamp">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={(e) => { e.stopPropagation(); setPreviewUuid(record.uuid); setPreviewVariant('stamp'); setPreviewVisible(true); }}
            />
          </Tooltip>
          <Dropdown menu={{ items: downloadMenuItems(record) }} trigger={['click']} placement="bottomRight">
            <Tooltip title="Tải xuống">
              <Button type="text" size="small" icon={<DownloadOutlined />} onClick={(e) => e.stopPropagation()} />
            </Tooltip>
          </Dropdown>
        </Space>
      ),
    },
  ];

  return (
    <Container>
      <ControlRow>
        <RecordCount>
          {pagination.total} ảnh thu thập
          {currentProject?.id ? ` · Dự án #${currentProject.id}` : ''}
        </RecordCount>
        <ControlActions>
          <Tooltip title="Làm mới">
            <Button type="text" size="small" icon={<ReloadOutlined spin={loading} />} onClick={() => fetchPhotos(1, pagination.pageSize)} />
          </Tooltip>
          {onMaximize && (
            <Tooltip title={isMaximized ? 'Thu nhỏ' : 'Phóng to'}>
              <Button type="text" size="small" icon={isMaximized ? <CompressOutlined /> : <ExpandOutlined />} onClick={onMaximize} />
            </Tooltip>
          )}
          {onClose && (
            <Tooltip title="Đóng">
              <Button type="text" size="small" icon={<CloseOutlined />} onClick={onClose} />
            </Tooltip>
          )}
        </ControlActions>
      </ControlRow>

      <FilterRow>
        <Search
          placeholder="Tìm file, đối tượng, địa điểm..."
          allowClear
          size="small"
          style={{ flex: 1, minWidth: 120, maxWidth: 240 }}
          onSearch={(v) => setSearchText(v)}
          onChange={(e) => !e.target.value && setSearchText('')}
        />
        <RangePicker
          size="small"
          style={{ flex: 1, minWidth: 200 }}
          onChange={(_, strs) => setDateRange(strs[0] && strs[1] ? [strs[0], strs[1]] : null)}
        />
        <UI_MapToggle>
          <span style={{ fontSize: 11, color: '#64748b' }}>Bản đồ</span>
          <Switch size="small" checked={showOnMap} onChange={setShowOnMap} />
        </UI_MapToggle>
      </FilterRow>

      {/* Vector tile toggle — CollectPhotoVectorTileController in MapScreen reacts to showCollectPhotoVectorTilesAtom */}

      {previewVisible && previewBlobUrl && (
        <div style={{ display: 'none' }}>
          <Image.PreviewGroup
            preview={{
              visible: previewVisible,
              onVisibleChange: (v) => { setPreviewVisible(v); if (!v) setPreviewUuid(null); },
              current: 0,
            }}
          >
            <Image src={previewBlobUrl} />
          </Image.PreviewGroup>
        </div>
      )}

      <Spin spinning={loading}>
        {!loading && allRows.length === 0 ? (
          <UI_Empty>
            <Empty description="Không có ảnh thu thập nào" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </UI_Empty>
        ) : (
          <Table
            columns={columns}
            dataSource={allRows}
            rowKey="uuid"
            size="small"
            bordered
            scroll={{ y: tableHeight ? `calc(${tableHeight} - 100px)` : 300 }}
            pagination={{
              current: pagination.page,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: !isMobile,
              showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
              onChange: (page, pageSize) => fetchPhotos(page, pageSize),
            }}
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
              style: { cursor: record.location ? 'pointer' : 'default' },
            })}
          />
        )}
      </Spin>
    </Container>
  );
};

export default memo(CollectedPhotosTab);

/* ─── Styled Components ─────────────────────────────────────────────────────── */

const Container = styled.div`
  padding: 6px;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow: hidden;
  .ant-table { font-size: 12px; }
  .ant-table-thead > tr > th {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    font-weight: 600;
    font-size: 10px !important;
    color: #374151;
    border-bottom: 2px solid #e2e8f0;
    padding: 4px !important;
  }
  .ant-table-tbody > tr > td {
    padding: 4px !important;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
  }
  .ant-table-tbody > tr { transition: all 0.2s ease; }
  .ant-table-tbody > tr:hover > td { background: rgba(52, 211, 153, 0.05) !important; }
`;

const ControlRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 4px 0;
`;

const RecordCount = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #374151;
`;

const ControlActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const UI_ThumbWrapper = styled.div`
  width: 60px;
  height: 60px;
  overflow: hidden;
  border-radius: 4px;
  flex-shrink: 0;
  cursor: zoom-in;
`;

const UI_FileName = styled.div`
  font-size: 11px;
  color: #374151;
  display: flex;
  align-items: flex-start;
  gap: 2px;
  line-height: 1.4;
  span { word-break: break-word; white-space: normal; }
`;

const UI_ItemName = styled.div`
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const UI_Location = styled.div`
  font-size: 11px;
  display: flex;
  align-items: flex-start;
  gap: 2px;
  line-height: 1.4;
  span { word-break: break-word; white-space: normal; }
`;

const UI_UserCell = styled.div`
  font-size: 11px;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UI_MapToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 6px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  height: 24px;
`;

const UI_Empty = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
`;
